<script lang="ts" setup>
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { app } from "@/fire";
import { ref, watch } from "vue";
import { string } from "yup";
import { useForm, useField } from "vee-validate";
import { useI18n } from "vue-i18n";
import { reportAuthProviderError } from "@/util/authProviderErrorReporting";

const emits = defineEmits(["postLogin", "changeState"]);

const props = defineProps({
  email: {
    type: String,
    required: false,
  },
});

const { t } = useI18n();

const auth = getAuth(app);
const error = ref("");
const providerHint = ref("");
const pendingProviderId = ref<string | null>(null);
const pendingEmail = ref<string | null>(null);

const { validate, errors } = useForm();
const { value: email } = useField<string>(
  "email",
  string()
    .email(t("login.email.error.invalid"))
    .required(t("login.email.error.missing")),
  { initialValue: props.email },
);

const supportedProviders = [
  { id: "google.com", labelKey: "login.providers.google" },
  { id: "apple.com", labelKey: "login.providers.apple" },
  { id: "microsoft.com", labelKey: "login.providers.microsoft" },
];

const normalizeEmail = (value: string) => value.trim().toLowerCase();

const getSingleProviderId = (methods: string[]) => {
  if (methods.length !== 1) {
    return null;
  }

  const method = methods[0];
  return (
    supportedProviders.find((provider) => provider.id === method)?.id ?? null
  );
};

const setPendingProvider = (emailValue: string, methods: string[]) => {
  const providerId = getSingleProviderId(methods);
  if (providerId) {
    pendingProviderId.value = providerId;
    pendingEmail.value = emailValue;
    return;
  }

  pendingProviderId.value = null;
  pendingEmail.value = null;
};

const buildProviderHint = (methods: string[]) => {
  const supported = supportedProviders.filter((provider) =>
    methods.includes(provider.id),
  );

  if (supported.length > 0) {
    const providersLabel = supported.map((provider) => t(provider.labelKey));
    if (supported.length === 1) {
      return t("login.providerHint.single", { provider: providersLabel[0] });
    }

    return t("login.providerHint.multiple", {
      providers: providersLabel.join(", "),
    });
  }

  if (methods.includes("emailLink")) {
    return t("login.providerHint.emailLink");
  }

  return t("login.providerHint.unknown");
};

watch(email, () => {
  providerHint.value = "";
  error.value = "";
  pendingProviderId.value = null;
  pendingEmail.value = null;
});

async function signInWithGoogle() {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    emits("postLogin", result);
  } catch (err: any) {
    error.value = err.message;
    reportAuthProviderError("google.com", err);
    console.error(err);
  }
}

async function signInWithApple() {
  try {
    const provider = new OAuthProvider("apple.com");
    const result = await signInWithPopup(auth, provider);
    emits("postLogin", result);
  } catch (err: any) {
    error.value = err.message;
    reportAuthProviderError("apple.com", err);
    console.error(err);
  }
}

async function signInWithMicrosoft() {
  try {
    const provider = new OAuthProvider("microsoft.com");
    const result = await signInWithPopup(auth, provider);
    emits("postLogin", result);
  } catch (err: any) {
    error.value = err.message;
    reportAuthProviderError("microsoft.com", err);
    console.error(err);
  }
}

const signInWithProvider = (providerId: string) => {
  if (providerId === "google.com") {
    void signInWithGoogle();
    return;
  }

  if (providerId === "apple.com") {
    void signInWithApple();
    return;
  }

  if (providerId === "microsoft.com") {
    void signInWithMicrosoft();
  }
};

const hasValidated = ref(false);

async function checkIfUserExists() {
  const currentEmail = normalizeEmail(email.value ?? "");
  if (pendingProviderId.value && pendingEmail.value === currentEmail) {
    signInWithProvider(pendingProviderId.value);
    return;
  }

  hasValidated.value = true;
  providerHint.value = "";
  error.value = "";
  const isValid = await validate();
  if (!isValid) {
    return;
  }

  try {
    const methods = await fetchSignInMethodsForEmail(auth, email.value);
    if (methods.length === 0) {
      setPendingProvider(currentEmail, methods);
      emits("changeState", "NEW_USER", email.value);
    } else if (methods.includes("password")) {
      setPendingProvider(currentEmail, []);
      emits("changeState", "PASSWORD", email.value);
    } else {
      providerHint.value = buildProviderHint(methods);
      setPendingProvider(currentEmail, methods);
    }
  } catch (err: any) {
    error.value = t("login.errors.fetchMethods");
    console.error(err);
  }
}
</script>

<template>
  <div class="login_container">
    <h2 class="login_header">{{ $t("login.header") }}</h2>

    <div class="button_container">
      <button @click="signInWithGoogle" class="button">
        <img
          class="firebaseui-idp-icon"
          alt=""
          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
        />
        <span class="text">{{ $t("login.withGoogle") }}</span>
      </button>
      <button @click="signInWithApple" class="button">
        <img
          class="firebaseui-idp-icon"
          alt=""
          src="@/assets/Apple_logo_black.svg"
        />
        <span class="text">{{ $t("login.withApple") }}</span>
      </button>
      <button @click="signInWithMicrosoft" class="button">
        <img
          class="firebaseui-idp-icon"
          alt=""
          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/microsoft.svg"
        />
        <span class="text">{{ $t("login.withMicrosoft") }}</span>
      </button>
    </div>

    <div class="divider">
      <span>{{ $t("login.or") }}</span>
    </div>

    <div>
      <form @submit.prevent="checkIfUserExists" class="email-form" novalidate>
        <label>{{ $t("login.email.label") }}</label>
        <div class="input_container">
          <input
            class="input"
            :class="{ invalid: errors.email !== undefined && hasValidated }"
            v-model="email"
            type="email"
            :placeholder="$t('login.email.placeholder')"
            autocomplete="username"
          />
          <p
            class="help"
            :class="{ visible: errors.email !== undefined && hasValidated }"
          >
            {{ errors.email }}
          </p>
          <p v-if="providerHint" class="hint">{{ providerHint }}</p>
          <p v-if="error" class="help visible">{{ error }}</p>
        </div>
        <button type="submit" class="button primary">
          {{ $t("login.continue") }}
        </button>
      </form>
    </div>

    <a href="https://apps.apple.com/de/app/pitchview-projector/id1501699612">
      <img class="badge" src="@/assets/app_store_badge.svg" />
    </a>
  </div>
</template>

<style src="./login.scss" scoped lang="scss"></style>

<style lang="scss" scoped>
.badge {
  height: 40px;
  margin-top: 20px;
}

.button .text {
  text-align: center;
  flex: 1;
}

.button_container .button img {
  border: none;
  background-color: var(--classic-brand-backplate, transparent);
  box-shadow: 0 0 0 2px var(--classic-brand-backplate, transparent);
  display: inline-block;
  height: 18px;
  vertical-align: middle;
  width: 16px;
  margin-right: 5px;
}

.divider {
  overflow: hidden;
  position: relative;
  text-align: center;
  margin: 0.5em 0 0.8em 0;
}

.divider::before,
.divider::after {
  background-color: var(--classic-border-subtle, hsla(36, 10%, 61%, 0.3));
  content: "";
  display: inline-block;
  height: 1px;
  position: relative;
  vertical-align: middle;
  width: 50%;
}

.divider::before {
  margin-left: -50%;
  right: 0;
}

.divider::after {
  left: 0;
  margin-right: -50%;
}

.divider span {
  padding: 0 0.9em;
}
</style>
